import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Videocam,
  VideocamOff,
  Mic,
  MicOff,
  Stop,
  Visibility,
  AttachMoney,
  Warning,
  FlipCameraIos,
  RadioButtonChecked as LiveIcon,
} from '@mui/icons-material';
// AWS IVS Web Broadcast SDK
import IVSBroadcastClient from 'amazon-ivs-web-broadcast';
import { useLiveStreamEvents } from '../../hooks/useLiveStreamEvents';
import liveStreamService, { 
  StreamCredentials, 
  LiveStreamEmoji 
} from '../../services/live-stream-service';
import { logger } from '../../utils/logger';

/**
 * Styled container for the broadcaster interface
 */
const BroadcasterContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100vh',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
}));

/**
 * Styled video preview container
 */
const VideoPreviewContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  flex: 1,
  backgroundColor: '#000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}));

/**
 * Styled video element
 */
const VideoPreview = styled('video')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}));

/**
 * Styled controls container
 */
const ControlsContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

/**
 * Styled metrics container
 */
const MetricsContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  
  [theme.breakpoints.down('sm')]: {
    top: theme.spacing(1),
    left: theme.spacing(1),
  },
}));

/**
 * Styled alert container for moderation warnings
 */
const AlertContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  maxWidth: 300,
  zIndex: 20,
  
  [theme.breakpoints.down('sm')]: {
    top: theme.spacing(1),
    right: theme.spacing(1),
    left: theme.spacing(1),
    maxWidth: 'none',
  },
}));

/**
 * Stream state enum
 */
enum StreamState {
  IDLE = 'idle',
  STARTING = 'starting',
  LIVE = 'live',
  STOPPING = 'stopping',
  ERROR = 'error',
}

/**
 * Stream metrics interface
 */
interface StreamMetrics {
  viewerCount: number;
  totalViewers: number;
  duration: number;
  adRevenue: number;
  emojiCount: number;
}

/**
 * Props for LiveStreamBroadcaster component
 */
interface LiveStreamBroadcasterProps {
  /** Stream ID for the broadcast */
  streamId: string;
  /** Stream title */
  title: string;
  /** Pre-fetched stream credentials (for instant streams that are already created) */
  initialCredentials?: import('../../services/live-stream-service').StreamCredentials;
  /** Callback when stream ends */
  onStreamEnd: () => void;
  /** Callback when stream starts successfully */
  onStreamStart?: () => void;
  /** Callback when error occurs */
  onError?: (error: string) => void;
  /** Whether to show ad revenue tracking */
  showAdRevenue?: boolean;
  /** Whether to enable content moderation alerts */
  enableModeration?: boolean;
}

/**
 * Live stream broadcaster component with IVS integration
 */
const LiveStreamBroadcaster: React.FC<LiveStreamBroadcasterProps> = ({
  streamId,
  title,
  initialCredentials,
  onStreamEnd,
  onStreamStart,
  onError,
  showAdRevenue = true,
  enableModeration = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const broadcastClientRef = useRef<any>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const metricsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // State
  const [streamState, setStreamState] = useState<StreamState>(StreamState.IDLE);
  const [credentials, setCredentials] = useState<StreamCredentials | null>(null);
  const [metrics, setMetrics] = useState<StreamMetrics>({
    viewerCount: 0,
    totalViewers: 0,
    duration: 0,
    adRevenue: 0,
    emojiCount: 0,
  });
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [moderationAlert, setModerationAlert] = useState<string | null>(null);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Real-time emoji reactions via native IVS Chat SDK
  const { emojis: liveEmojis, sendLocalEmoji: sendBroadcasterEmoji } = useLiveStreamEvents({
    streamId,
    chatToken: credentials?.chatToken,
    enabled: streamState === StreamState.LIVE,
  });

  /**
   * Initialize real IVS broadcast client
   */
  const initializeBroadcastClient = useCallback(async () => {
    try {
      logger.info('Initializing real IVS broadcast client');

      // Create IVS broadcast client using npm package
      // Use BASIC_LANDSCAPE config since IVS channels are created as BASIC type
      const client = IVSBroadcastClient.create({
        streamConfig: IVSBroadcastClient.BASIC_LANDSCAPE,
      });

      // Set up event listeners for broadcast state changes
      // Use string event names to avoid type issues with the SDK's strict Callback type
      (client as any).on('clientError', (err: any) => {
        logger.error('IVS Broadcast error:', err);
        setError(`Broadcast error: ${err?.message || 'Unknown error'}`);
        setStreamState(StreamState.ERROR);
      });

      (client as any).on('connectionStateChange', (state: string) => {
        logger.info('IVS Connection state changed:', state);
        if (state === 'connected') {
          setStreamState(StreamState.LIVE);
          if (onStreamStart) {
            onStreamStart();
          }
        }
      });

      (client as any).on('activeStateChange', (active: boolean) => {
        logger.info('IVS Active state changed:', active);
      });

      broadcastClientRef.current = client;
      logger.info('IVS broadcast client initialized successfully');
      return client;
    } catch (error) {
      logger.error('Failed to initialize broadcast client:', error);
      throw error;
    }
  }, [onStreamStart]);

  /**
   * Get user media and create local stream
   */
  const setupLocalStream = useCallback(async () => {
    try {
      logger.info('Requesting camera and microphone access...');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
          facingMode: 'user',
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      logger.info('Camera and microphone access granted');

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true; // Prevent feedback
        
        // Ensure video plays
        try {
          await videoRef.current.play();
          logger.info('Video preview started successfully');
        } catch (playError) {
          logger.warn('Video autoplay failed, user interaction may be required:', playError);
          // Try to play again after a short delay
          setTimeout(async () => {
            try {
              if (videoRef.current) {
                await videoRef.current.play();
              }
            } catch (retryError) {
              logger.error('Video play retry failed:', retryError);
            }
          }, 1000);
        }
      }

      // Store the stream for later cleanup
      localStreamRef.current = stream as any;

      return stream;
    } catch (error: any) {
      logger.error('Failed to get user media:', error);
      
      let errorMessage = 'Camera and microphone access required for live streaming.';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Camera and microphone access denied. Please allow access and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera or microphone found. Please connect a camera and microphone.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera or microphone is already in use by another application.';
      }
      
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Start the real live stream
   */
  const startStream = useCallback(async () => {
    try {
      setStreamState(StreamState.STARTING);
      setError(null);

      let streamCredentials;
      
      if (initialCredentials) {
        // Use pre-fetched credentials (instant stream already created)
        logger.info('Using pre-fetched stream credentials from instant stream creation');
        streamCredentials = initialCredentials;
      } else {
        // Get credentials from backend (scheduled stream flow)
        logger.info('Getting stream credentials from backend...');
        streamCredentials = await liveStreamService.startStream(streamId);
      }
      
      setCredentials(streamCredentials);
      
      logger.info('Stream credentials received:', {
        hasStreamKey: !!streamCredentials.streamKey,
        hasIngestEndpoint: !!streamCredentials.ingestEndpoint,
        hasPlaybackUrl: !!streamCredentials.playbackUrl
      });

      // Initialize real IVS broadcast client
      const client = await initializeBroadcastClient();
      
      // Setup local stream (camera and microphone)
      const mediaStream = await setupLocalStream();
      
      // Add camera and microphone to the broadcast client
      // IVS SDK expects MediaStream objects for video and audio
      const videoStream = new MediaStream(mediaStream.getVideoTracks());
      const audioStream = new MediaStream(mediaStream.getAudioTracks());
      
      if (videoStream.getVideoTracks().length > 0) {
        await client.addVideoInputDevice(videoStream, 'camera', { index: 0 });
        logger.info('Video stream added to broadcast client');
      }
      
      if (audioStream.getAudioTracks().length > 0) {
        await client.addAudioInputDevice(audioStream, 'microphone');
        logger.info('Audio stream added to broadcast client');
      }

      // Start real broadcasting to AWS IVS
      logger.info('Starting real broadcast to AWS IVS...');
      await client.startBroadcast(streamCredentials.streamKey, streamCredentials.ingestEndpoint);
      
      // Start metrics polling
      startMetricsPolling();

      logger.info('Real live stream started successfully');

    } catch (error) {
      logger.error('Failed to start real stream:', error);
      setError(error instanceof Error ? error.message : 'Failed to start stream');
      setStreamState(StreamState.ERROR);
      if (onError) {
        onError(error instanceof Error ? error.message : 'Failed to start stream');
      }
    }
  }, [streamId, initialCredentials, initializeBroadcastClient, setupLocalStream, onStreamStart, onError]);

  /**
   * Stop the real live stream
   */
  const stopStream = useCallback(async () => {
    try {
      setStreamState(StreamState.STOPPING);
      logger.info('Stopping real live stream...');

      // Stop metrics polling
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
        metricsIntervalRef.current = null;
      }

      // Stop real IVS broadcast
      if (broadcastClientRef.current) {
        logger.info('Stopping IVS broadcast...');
        await broadcastClientRef.current.stopBroadcast();
        broadcastClientRef.current.delete();
        broadcastClientRef.current = null;
      }

      // Stop local media stream
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => {
          track.stop();
          logger.info(`Stopped ${track.kind} track`);
        });
        videoRef.current.srcObject = null;
      }

      // End stream on backend (non-blocking - stream is already stopped locally)
      logger.info('Ending stream on backend...');
      try {
        await liveStreamService.endStream(streamId);
        logger.info('Stream ended on backend successfully');
      } catch (endError: any) {
        // If stream is already ended (400) or not found (404), that's fine
        logger.warn('Backend end-stream returned error (stream may already be ended):', endError);
      }

      setStreamState(StreamState.IDLE);
      setCredentials(null);
      onStreamEnd();

      logger.info('Real live stream stopped successfully');

    } catch (error) {
      logger.error('Failed to stop real stream:', error);
      // Even if there's an error, try to end gracefully
      setStreamState(StreamState.IDLE);
      setCredentials(null);
      onStreamEnd();
    }
  }, [streamId, onStreamEnd]);

  /**
   * Toggle video on/off
   */
  const toggleVideo = useCallback(async () => {
    try {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.enabled = !isVideoEnabled;
          setIsVideoEnabled(!isVideoEnabled);
          logger.info(`Video ${!isVideoEnabled ? 'enabled' : 'disabled'}`);
        }
      }
    } catch (error) {
      logger.error('Failed to toggle video:', error);
    }
  }, [isVideoEnabled]);

  /**
   * Toggle audio on/off
   */
  const toggleAudio = useCallback(async () => {
    try {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = !isAudioEnabled;
          setIsAudioEnabled(!isAudioEnabled);
          logger.info(`Audio ${!isAudioEnabled ? 'enabled' : 'disabled'}`);
        }
      }
    } catch (error) {
      logger.error('Failed to toggle audio:', error);
    }
  }, [isAudioEnabled]);

  /**
   * Flip camera (front/back)
   */
  const flipCamera = useCallback(async () => {
    try {
      // This would require re-initializing the stream with different constraints
      // Implementation depends on device capabilities
      logger.info('Camera flip requested');
    } catch (error) {
      logger.error('Failed to flip camera:', error);
    }
  }, []);

  /**
   * Start polling for real stream metrics
   */
  const startMetricsPolling = useCallback(() => {
    const startTime = Date.now();
    let lastViewerCount = 0;
    
    metricsIntervalRef.current = setInterval(async () => {
      try {
        // Calculate duration locally
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        
        // Poll viewer count from discover endpoint every 15 seconds
        if (elapsedSeconds % 15 < 5) {
          try {
            const streams = await liveStreamService.discoverStreams({ limit: 50, includeScheduled: false, includeTrending: false });
            const current = [...streams.liveStreams, ...streams.trendingStreams].find(s => s.streamId === streamId);
            if (current) {
              lastViewerCount = (current as any).currentViewers || current.totalViewers || 0;
            }
          } catch { /* ignore polling errors */ }
        }
        
        setMetrics(prev => ({
          ...prev,
          duration: elapsedSeconds,
          viewerCount: lastViewerCount,
        }));
      } catch (error) {
        logger.error('Failed to update stream metrics:', error);
      }
    }, 5000);
  }, [streamId]);

  /**
   * Handle emoji sent
   */
  const handleEmojiSent = useCallback((emoji: LiveStreamEmoji) => {
    setMetrics(prev => ({
      ...prev,
      emojiCount: prev.emojiCount + 1,
    }));
  }, []);

  /**
   * Handle moderation alert
   */
  const handleModerationAlert = useCallback((alert: string) => {
    setModerationAlert(alert);
    // Auto-hide after 10 seconds
    setTimeout(() => setModerationAlert(null), 10000);
  }, []);

  /**
   * Format duration for display
   */
  const formatDuration = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, []);

  /**
   * Initialize camera preview when component mounts
   */
  useEffect(() => {
    // Don't auto-start the full stream, just setup camera preview
    if (streamState === StreamState.IDLE) {
      setupCameraPreview();
    }
  }, []);

  /**
   * Setup camera preview without starting the stream
   */
  const setupCameraPreview = useCallback(async () => {
    try {
      setStreamState(StreamState.STARTING);
      setError(null);
      
      // Just setup the camera preview, don't start broadcasting yet
      await setupLocalStream();
      
      // Set state to show camera is ready
      setStreamState(StreamState.IDLE);
      
    } catch (error) {
      logger.error('Failed to setup camera preview:', error);
      setError(error instanceof Error ? error.message : 'Failed to access camera');
      setStreamState(StreamState.ERROR);
    }
  }, [setupLocalStream]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
      }
      
      // Stop broadcast on cleanup
      if (broadcastClientRef.current) {
        broadcastClientRef.current.stopBroadcast().catch(console.error);
      }
      
      // Cleanup local stream
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <BroadcasterContainer>
      {/* Video Preview */}
      <VideoPreviewContainer>
        <VideoPreview
          ref={videoRef}
          autoPlay
          playsInline
          muted
        />
        
        {/* Loading overlay */}
        {streamState === StreamState.STARTING && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            sx={{ transform: 'translate(-50%, -50%)' }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" color="white" textAlign="center" mt={2}>
              Accessing Camera...
            </Typography>
          </Box>
        )}

        {/* No camera preview message */}
        {streamState === StreamState.ERROR && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            sx={{ transform: 'translate(-50%, -50%)', textAlign: 'center' }}
          >
            <Typography variant="h6" color="white" mb={2}>
              Camera Not Available
            </Typography>
            <Typography variant="body2" color="white" mb={3}>
              {error || 'Please allow camera access to start broadcasting'}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={setupCameraPreview}
              startIcon={<Videocam />}
            >
              Try Again
            </Button>
          </Box>
        )}

        {/* Floating Emoji Reactions from viewers via IVS Chat */}
        {liveEmojis.map((emoji) => (
          <Box
            key={emoji.id}
            sx={{
              position: 'absolute',
              left: `${emoji.x}%`,
              top: `${emoji.y}%`,
              fontSize: '2.5rem',
              pointerEvents: 'none',
              zIndex: 22,
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              animation: 'floatUpEmoji 3s ease-out forwards',
              '@keyframes floatUpEmoji': {
                '0%': { transform: 'translateY(0) scale(1)', opacity: 1 },
                '50%': { transform: 'translateY(-60px) scale(1.3)', opacity: 0.8 },
                '100%': { transform: 'translateY(-120px) scale(0.6)', opacity: 0 },
              },
            }}
          >
            {emoji.emoji}
          </Box>
        ))}

        {/* Stream Metrics */}
        <MetricsContainer>
          <Chip
            icon={<Visibility />}
            label={`${metrics.viewerCount} viewers`}
            color="primary"
            size="small"
          />
          <Chip
            label={formatDuration(metrics.duration)}
            color="secondary"
            size="small"
          />
          {showAdRevenue && (
            <Chip
              icon={<AttachMoney />}
              label={`$${metrics.adRevenue.toFixed(2)}`}
              color="success"
              size="small"
            />
          )}
        </MetricsContainer>

        {/* Moderation Alert */}
        {enableModeration && moderationAlert && (
          <AlertContainer>
            <Alert
              severity="warning"
              icon={<Warning />}
              onClose={() => setModerationAlert(null)}
            >
              {moderationAlert}
            </Alert>
          </AlertContainer>
        )}

        {/* Error Alert */}
        {error && (
          <AlertContainer>
            <Alert
              severity="error"
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </AlertContainer>
        )}

        {/* Stream Controls */}
        <ControlsContainer>
          <Box display="flex" gap={1} alignItems="center">
            {streamState === StreamState.IDLE && !localStreamRef.current && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={setupCameraPreview}
                startIcon={<Videocam />}
              >
                Enable Camera
              </Button>
            )}
            
            {streamState === StreamState.IDLE && localStreamRef.current && (
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={startStream}
                startIcon={<LiveIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FF5252, #26C6DA)',
                  },
                }}
              >
                Go Live!
              </Button>
            )}
            
            {streamState === StreamState.STARTING && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={24} color="inherit" />
                <Typography color="white">
                  Accessing camera and microphone...
                </Typography>
              </Box>
            )}
            
            {streamState === StreamState.LIVE && (
              <>
                <IconButton
                  color={isVideoEnabled ? "primary" : "error"}
                  onClick={toggleVideo}
                  size="large"
                  sx={{ 
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                  }}
                >
                  {isVideoEnabled ? <Videocam /> : <VideocamOff />}
                </IconButton>
                
                <IconButton
                  color={isAudioEnabled ? "primary" : "error"}
                  onClick={toggleAudio}
                  size="large"
                  sx={{ 
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                  }}
                >
                  {isAudioEnabled ? <Mic /> : <MicOff />}
                </IconButton>
                
                {isMobile && (
                  <IconButton
                    color="primary"
                    onClick={flipCamera}
                    size="large"
                    sx={{ 
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                    }}
                  >
                    <FlipCameraIos />
                  </IconButton>
                )}
                
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  onClick={() => setShowEndDialog(true)}
                  startIcon={<Stop />}
                  sx={{
                    backgroundColor: 'rgba(244, 67, 54, 0.9)',
                    '&:hover': { backgroundColor: 'rgba(244, 67, 54, 1)' }
                  }}
                >
                  End Stream
                </Button>
              </>
            )}
            
            {streamState === StreamState.ERROR && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={startStream}
                startIcon={<Videocam />}
              >
                Retry
              </Button>
            )}
          </Box>

          {/* Stream Title and Status */}
          <Box textAlign="center">
            <Typography variant="h6" color="white">
              {title}
            </Typography>
            {streamState === StreamState.LIVE && (
              <Chip 
                label="LIVE" 
                color="error" 
                size="small" 
                sx={{ mt: 0.5, fontWeight: 'bold' }}
              />
            )}
          </Box>
        </ControlsContainer>
      </VideoPreviewContainer>

      {/* End Stream Confirmation Dialog */}
      <Dialog open={showEndDialog} onClose={() => setShowEndDialog(false)}>
        <DialogTitle>End Live Stream?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to end your live stream? This will disconnect all viewers.
          </Typography>
          {metrics.viewerCount > 0 && (
            <Typography color="text.secondary" mt={1}>
              You currently have {metrics.viewerCount} viewers watching.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEndDialog(false)}>
            Cancel
          </Button>
          <Button onClick={stopStream} color="error" variant="contained">
            End Stream
          </Button>
        </DialogActions>
      </Dialog>
    </BroadcasterContainer>
  );
};

export default LiveStreamBroadcaster;